import { cn } from "@/lib/utils";
import { X } from "lucide-react";

/**
 * Shared floating bulk-action bar — the single bulk-action surface
 * used across every list with multi-select (work items, time
 * entries, …).
 *
 * Layout is fixed: `N selected · [caller's action controls] · clear`,
 * pinned to the bottom-centre of the viewport. The bar hides itself
 * when nothing is selected, so callers can render it unconditionally.
 *
 * The action controls themselves are caller-supplied `children` —
 * each list has its own verbs (status / assignee / priority / delete
 * vs. approve / reject / billing) — but the chrome (placement,
 * surface, the "N selected" count, the clear affordance) is shared
 * so every bulk bar looks and sits identically.
 */
export function BulkActionBar({
  selectedCount,
  onClear,
  children,
  itemNoun = "item",
  itemNounPlural,
  totalCount,
  stackIndex = 0,
}: {
  selectedCount: number;
  onClear: () => void;
  children: React.ReactNode;
  /** Singular noun for the count label. */
  itemNoun?: string;
  /** Plural noun — defaults to `itemNoun + "s"`. */
  itemNounPlural?: string;
  /** When the same selection drives several bars (one per applicable
   *  action group), pass the total selection count — the label then
   *  reads "X/Y items" instead of just "X". Defaults to undefined for
   *  the single-bar use case. */
  totalCount?: number;
  /** Bottom-stack slot — 0 sits at the standard bottom-4 position,
   *  1 stacks above it, etc. Lets callers render several bars at once
   *  without overlap. */
  stackIndex?: number;
}) {
  if (selectedCount === 0) return null;

  const noun = selectedCount === 1 ? itemNoun : (itemNounPlural ?? `${itemNoun}s`);
  // Bar height (~44px) + 12px gap. Inlined as a style so callers can
  // stack any number of bars without us needing to mint a Tailwind
  // class per slot.
  const STACK_GAP_PX = 56;
  const bottomPx = 16 + stackIndex * STACK_GAP_PX;

  return (
    <div
      data-testid="bulk-action-bar"
      style={{ bottom: `${bottomPx}px` }}
      className={cn(
        "fixed left-1/2 z-50 -translate-x-1/2",
        "flex items-center gap-2 rounded-xl border bg-card px-4 py-2.5 shadow-xl",
      )}
    >
      <span className="mr-1 text-sm font-medium text-muted-foreground">
        {typeof totalCount === "number" && totalCount !== selectedCount
          ? `${selectedCount}/${totalCount} ${noun} selected`
          : `${selectedCount} ${noun} selected`}
      </span>

      <div className="h-5 w-px bg-border" aria-hidden="true" />

      {children}

      <div className="h-5 w-px bg-border" aria-hidden="true" />

      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
        Clear
      </button>
    </div>
  );
}
